/**
 * Membership landing page — Bell Tower Privilege Membership (illustrative).
 *
 * Route:  /hub/:slug/membership
 *
 * Public. Mobile-first. Wrapped in <GuestLoyaltyThemeProvider mode="dark">.
 *
 * ── CONTENT STRUCTURE ─────────────────────────────────────────────
 * All offer content lives in a single exported config object
 * (`MEMBERSHIP_OFFER_CONFIG`) at the top of this file. Structure
 * mirrors the real Bell Tower Privilege Membership kit so replacing
 * copy is a data-only edit — no layout change required.
 *
 * The values below are OBVIOUSLY placeholder (title tags like
 * "PLACEHOLDER ·" and rupee figures written as "₹ X"). They MUST be
 * replaced before launch. The `illustrative_disclaimer` is production
 * copy and stays as-is.
 * ─────────────────────────────────────────────────────────────────
 */
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Sparkles, CreditCard, Ticket, Users, Gift, Check } from 'lucide-react';

import { GuestLoyaltyThemeProvider } from '../components/loyalty/ThemeProvider';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';

const API = process.env.REACT_APP_BACKEND_URL;

/* ================================================================
 *   MEMBERSHIP_OFFER_CONFIG
 *
 *   PLACEHOLDER — replace every value below with the real Bell Tower
 *   Privilege Membership copy before launch. Structure must not
 *   change; only values.
 *
 *   Field guide:
 *     • welcome_offers      → visit-triggered offers on joining
 *     • privilege_card      → the percentage discount on card payments
 *     • voucher_book        → individual vouchers, each with a title,
 *                             description and rupee value
 *     • group_and_party     → buffet vouchers for group bookings
 *     • buy_one_offers      → BOGO-style coupon set
 *     • illustrative_disclaimer → PRODUCTION copy; ships as-is
 * ================================================================ */
export const MEMBERSHIP_OFFER_CONFIG = {
  brand_name: 'Bell Tower Privilege Membership',
  tagline: 'PLACEHOLDER · replace with the real Bell Tower tagline before launch',
  price: {
    title: 'PLACEHOLDER · Membership fee',
    value: '₹ X',
    note:  'PLACEHOLDER · replace with the real annual fee before launch',
  },
  welcome_offers: [
    { title: 'PLACEHOLDER · Welcome offer #1',
      description: 'PLACEHOLDER · describe the offer triggered on the first visit',
      value: '₹ X' },
    { title: 'PLACEHOLDER · Welcome offer #2',
      description: 'PLACEHOLDER · describe the offer triggered on the second visit',
      value: '₹ X' },
  ],
  privilege_card: {
    title: 'PLACEHOLDER · Privilege card',
    description: 'PLACEHOLDER · describe the ongoing percentage discount on card payments',
    value: 'X%',
  },
  voucher_book: [
    { title: 'PLACEHOLDER · Voucher #1',
      description: 'PLACEHOLDER · describe voucher #1',
      value: '₹ X' },
    { title: 'PLACEHOLDER · Voucher #2',
      description: 'PLACEHOLDER · describe voucher #2',
      value: '₹ X' },
    { title: 'PLACEHOLDER · Voucher #3',
      description: 'PLACEHOLDER · describe voucher #3',
      value: '₹ X' },
  ],
  group_and_party: [
    { title: 'PLACEHOLDER · Group booking buffet voucher',
      description: 'PLACEHOLDER · describe the group booking voucher',
      value: '₹ X' },
  ],
  buy_one_offers: [
    { title: 'PLACEHOLDER · BOGO coupon #1',
      description: 'PLACEHOLDER · describe the buy-one-get-one coupon #1',
      value: 'Buy 1 Get 1' },
    { title: 'PLACEHOLDER · BOGO coupon #2',
      description: 'PLACEHOLDER · describe the buy-one-get-one coupon #2',
      value: 'Buy 1 Get 1' },
  ],
  /* PRODUCTION copy — required by the sprint. Ships as written. */
  illustrative_disclaimer:
    'All offers, values and privileges shown on this page are illustrative and ' +
    'subject to change. Actual entitlements will be confirmed at the time of ' +
    'enrolment. This form only registers your interest; it does not enrol you ' +
    'in the programme.',
};


/**
 * Fixed metadata for the five built-in section keys.
 * Public headings live here — not in the DB — so a client cannot
 * accidentally rename "Voucher book" into something confusing.
 */
const BUILTIN_META = {
  welcome_offers:  { heading: 'Welcome offers',           icon: 'Gift' },
  privilege_card:  { heading: 'Privilege card',           icon: 'CreditCard' },
  voucher_book:    { heading: 'Voucher book',             icon: 'Ticket' },
  group_and_party: { heading: 'Group & party bookings',   icon: 'Users' },
  buy_one_offers:  { heading: 'Buy-one coupons',          icon: 'Sparkles' },
};

/**
 * Reduce admin-authored + placeholder-fallback content into:
 *   { brand_name, tagline, price, sections: [renderable] }
 *
 * Each `renderable` is `{id, kind, key?, heading, subheading, icon, rows}`
 * ready for the render loop. Empty-row sections stay in the list; the
 * render loop is responsible for hiding them.
 */
function mergeContent(fallback, saved) {
  const brand = pick(saved?.title, fallback.brand_name);
  const tagline = pick(saved?.tagline, fallback.tagline);
  const price = {
    title: pick(saved?.price_title, fallback.price.title),
    value: pick(saved?.price_value, fallback.price.value),
    note:  pick(saved?.price_note,  fallback.price.note),
  };

  const sections = Array.isArray(saved?.sections) && saved.sections.length > 0
    ? saved.sections.map(coerceSaved)
    : legacyOrderFromFallback(fallback);

  return { brand_name: brand, tagline, price, sections };
}

function pick(v, fb) {
  return v === undefined || v === null || v === '' ? fb : v;
}

function coerceSaved(s) {
  const isBuiltin = s.kind === 'builtin' && BUILTIN_META[s.key];
  const meta = isBuiltin ? BUILTIN_META[s.key] : { heading: s.heading || '', icon: 'Sparkles' };
  return {
    id:         s.id || `${s.kind}-${Math.random().toString(36).slice(2, 8)}`,
    kind:       isBuiltin ? 'builtin' : 'custom',
    key:        isBuiltin ? s.key : null,
    heading:    isBuiltin ? meta.heading : (s.heading || 'Untitled section'),
    subheading: isBuiltin ? '' : (s.subheading || ''),
    icon:       meta.icon,
    rows:       Array.isArray(s.rows) ? s.rows : [],
  };
}

/**
 * When nothing has been saved yet, walk the placeholder config in the
 * historical order so the page still shows something meaningful.
 */
function legacyOrderFromFallback(fb) {
  return [
    { id: 'fb-welcome',   kind: 'builtin', key: 'welcome_offers',
      heading: BUILTIN_META.welcome_offers.heading,  subheading: '',
      icon: BUILTIN_META.welcome_offers.icon,        rows: fb.welcome_offers  || [] },
    { id: 'fb-privilege', kind: 'builtin', key: 'privilege_card',
      heading: BUILTIN_META.privilege_card.heading,  subheading: '',
      icon: BUILTIN_META.privilege_card.icon,
      rows: fb.privilege_card && fb.privilege_card.title ? [fb.privilege_card] : [] },
    { id: 'fb-voucher',   kind: 'builtin', key: 'voucher_book',
      heading: BUILTIN_META.voucher_book.heading,    subheading: '',
      icon: BUILTIN_META.voucher_book.icon,          rows: fb.voucher_book    || [] },
    { id: 'fb-party',     kind: 'builtin', key: 'group_and_party',
      heading: BUILTIN_META.group_and_party.heading, subheading: '',
      icon: BUILTIN_META.group_and_party.icon,       rows: fb.group_and_party || [] },
    { id: 'fb-bogo',      kind: 'builtin', key: 'buy_one_offers',
      heading: BUILTIN_META.buy_one_offers.heading,  subheading: '',
      icon: BUILTIN_META.buy_one_offers.icon,        rows: fb.buy_one_offers  || [] },
  ];
}


export default function GuestHubMembership() {
  const { slug } = useParams();
  const [hub, setHub] = useState({ loading: true, error: '', outlet: null, theme: null, content: null });
  const [form, setForm] = useState({ phone: '', name: '', consent: false });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/api/public/hub/${encodeURIComponent(slug || '')}`)
      .then((res) => {
        if (!alive) return;
        setHub({
          loading: false, error: '',
          outlet: res.data.outlet, theme: res.data.theme,
          content: res.data.membership_content || null,
        });
      })
      .catch(() => {
        if (alive) setHub({ loading: false, error: 'Hub not found', outlet: null, theme: null, content: null });
      });
    return () => { alive = false; };
  }, [slug]);

  const phoneDigits = (form.phone.match(/\d/g) || []).join('');
  const phoneValid = phoneDigits.length === 10;
  const canSubmit = phoneValid && form.consent && !submitting;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/api/public/hub/${encodeURIComponent(slug)}/membership-lead`,
        { phone: phoneDigits, name: form.name.trim(), consent: true },
      );
      setSubmitted(true);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (hub.loading) {
    return (
      <GuestLoyaltyThemeProvider mode="dark"
                                 className="min-h-screen w-full flex items-center justify-center px-6">
        <div className="text-sm opacity-70" data-testid="membership-loading">Loading…</div>
      </GuestLoyaltyThemeProvider>
    );
  }

  if (hub.error || !hub.outlet) {
    return (
      <GuestLoyaltyThemeProvider mode="dark"
                                 className="min-h-screen w-full flex items-center justify-center px-6">
        <div className="max-w-sm text-center" data-testid="membership-error">
          <div className="text-lg font-medium mb-1">Not available</div>
          <div className="text-sm opacity-70">This membership page is no longer active.</div>
        </div>
      </GuestLoyaltyThemeProvider>
    );
  }

  const cfg = mergeContent(MEMBERSHIP_OFFER_CONFIG, hub.content);
  const ICONS = { Gift, CreditCard, Ticket, Users, Sparkles };

  return (
    <GuestLoyaltyThemeProvider config={hub.theme} mode="dark"
                               className="min-h-screen w-full">
      {/* Ambient warm-gold wash so the background never feels flat. */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            `radial-gradient(100% 40% at 50% 0%, ${_alpha('--gold', 0.10)}, transparent 60%),` +
            `radial-gradient(80% 40% at 100% 100%, ${_alpha('--primary', 0.35)}, transparent 55%)`,
        }}
      />

      <div className="relative mx-auto w-full px-5 sm:px-6 pt-8 pb-24 md:max-w-xl"
           data-testid="membership-page">
        {/* Back link — comfortable touch target */}
        <Link to={`/hub/${slug}`}
              className="inline-flex items-center gap-1.5 text-sm opacity-75 hover:opacity-100 mb-8
                         min-h-[44px] py-2 -mx-2 px-2 rounded-lg"
              data-testid="membership-back">
          <ArrowLeft className="w-4 h-4" /> Back to hub
        </Link>

        {/* Hero */}
        <header className="guest-shimmer mb-10 rounded-3xl p-6 border"
                style={{
                  borderColor: _alpha('--gold', 0.22),
                  background: `linear-gradient(180deg, ${_alpha('--gold', 0.07)} 0%, transparent 100%)`,
                }}
                data-testid="membership-hero">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.32em] mb-4"
               style={{ color: 'var(--gold)' }}>
            <Sparkles className="w-3.5 h-3.5" /> Privilege Membership
          </div>
          <h1 className="text-4xl sm:text-5xl font-serif leading-tight"
              style={{ color: 'var(--ink)' }}>
            {cfg.brand_name}
          </h1>
          <div className="mt-3 h-[2px] w-12 rounded-full"
               style={{ background: 'var(--gold)' }} />
          {cfg.tagline ? (
            <div className="text-[15px] opacity-75 mt-4 leading-relaxed">{cfg.tagline}</div>
          ) : null}
          {(cfg.price.title || cfg.price.value) ? (
            <div className="mt-6 flex items-baseline gap-3 flex-wrap">
              {cfg.price.title ? (
                <div className="text-[11px] uppercase tracking-[0.28em] opacity-70">
                  {cfg.price.title}
                </div>
              ) : null}
              {cfg.price.value ? (
                <div className="text-3xl sm:text-4xl font-serif"
                     style={{ color: 'var(--gold)' }}>
                  {cfg.price.value}
                </div>
              ) : null}
            </div>
          ) : null}
          {cfg.price.note ? (
            <div className="text-xs opacity-60 mt-2">{cfg.price.note}</div>
          ) : null}
        </header>

        {/* Ordered sections — walk the array in saved order. Each
            section with 0 rows is silently hidden. Built-in sections
            keep their canonical heading + icon; custom sections use
            the admin-authored heading and a neutral sparkle icon. */}
        {cfg.sections.map((section) => {
          if (!section.rows || section.rows.length === 0) return null;
          if (section.kind === 'builtin' && section.key === 'privilege_card') {
            const row = section.rows[0];
            if (!row || !row.title) return null;
            return <PrivilegeCardSection key={section.id} card={row} />;
          }
          const Icon = ICONS[section.icon] || Sparkles;
          return (
            <OfferSection
              key={section.id}
              testid={testidFor(section)}
              icon={Icon}
              heading={section.heading}
              subheading={section.subheading}
              entries={section.rows}
            />
          );
        })}

        {/* Illustrative-offers disclaimer — production copy, legible but subdued.
            Always renders, regardless of what is configured; never reorderable. */}
        <div
          className="mt-10 mb-8 text-[13px] leading-relaxed rounded-xl px-4 py-4 border"
          style={{
            color: 'var(--ink-muted)',
            borderColor: _alpha('--gold', 0.18),
            background: _alpha('--primary-deep', 0.35),
          }}
          data-testid="membership-disclaimer"
        >
          {MEMBERSHIP_OFFER_CONFIG.illustrative_disclaimer}
        </div>

        {/* Lead capture form — distinct block, heavier border. */}
        <div
          className="rounded-2xl p-6 border-2"
          style={{
            borderColor: _alpha('--gold', 0.35),
            background: _alpha('--primary-deep', 0.5),
            boxShadow: `0 20px 60px ${_alpha('--primary', 0.35)}`,
          }}
          data-testid="membership-form-card"
        >
          {submitted ? (
            <div className="text-center py-4" data-testid="membership-form-success">
              <div
                className="mx-auto mb-4 h-12 w-12 rounded-full flex items-center justify-center"
                style={{ background: 'var(--gold)', color: 'var(--primary-deep)' }}
              >
                <Check className="w-6 h-6" strokeWidth={2.25} />
              </div>
              <div className="text-lg font-medium mb-1">You&apos;re on the list.</div>
              <div className="text-sm opacity-75">
                We&apos;ll reach out shortly with the next steps.
              </div>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-5" data-testid="membership-form">
              <div>
                <div className="text-base font-medium">Register your interest</div>
                <div className="text-xs opacity-65 mt-0.5">Takes ten seconds.</div>
              </div>

              {/* Phone — first field, 10 digits */}
              <div>
                <Label htmlFor="mem-phone"
                       className="text-[11px] uppercase tracking-[0.20em] opacity-75">
                  Phone (10 digits) *
                </Label>
                <Input
                  id="mem-phone"
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="98765 43210"
                  className="mt-1.5 h-12 text-[15px] rounded-lg bg-white/5
                             border text-white placeholder:text-white/45"
                  style={{ borderColor: _alpha('--gold', 0.30) }}
                  data-testid="membership-input-phone"
                />
              </div>

              {/* Name */}
              <div>
                <Label htmlFor="mem-name"
                       className="text-[11px] uppercase tracking-[0.20em] opacity-75">
                  Your name (optional)
                </Label>
                <Input
                  id="mem-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="First & last name"
                  className="mt-1.5 h-12 text-[15px] rounded-lg bg-white/5
                             border text-white placeholder:text-white/45"
                  style={{ borderColor: _alpha('--gold', 0.30) }}
                  data-testid="membership-input-name"
                />
              </div>

              {/* Outlet — display only */}
              <div>
                <Label className="text-[11px] uppercase tracking-[0.20em] opacity-75">
                  Outlet
                </Label>
                <div className="mt-1.5 px-3 h-12 flex items-center rounded-lg
                                text-[15px] border"
                     style={{
                       borderColor: _alpha('--gold', 0.20),
                       background: _alpha('--primary', 0.35),
                       color: _alpha('--ink', 0.9),
                     }}
                     data-testid="membership-outlet-display">
                  {hub.outlet.name}
                </div>
              </div>

              {/* Consent — bigger tap target */}
              <label className="flex items-start gap-3 cursor-pointer min-h-[48px]"
                     data-testid="membership-consent-label">
                <Checkbox
                  checked={form.consent}
                  onCheckedChange={(v) => setForm({ ...form, consent: !!v })}
                  className="mt-0.5 h-5 w-5 border-white/40 data-[state=checked]:bg-[var(--gold)]
                             data-[state=checked]:text-[var(--primary-deep)]
                             data-[state=checked]:border-[var(--gold)]"
                  data-testid="membership-consent"
                />
                <span className="text-[13px] opacity-85 leading-relaxed">
                  I agree to be contacted about the membership programme. My details will only be
                  used for this enquiry.
                </span>
              </label>

              <button
                type="submit"
                disabled={!canSubmit}
                className="guest-shimmer w-full font-medium rounded-lg
                           text-[15px] transition-all disabled:opacity-45"
                style={{
                  background: 'var(--gold)',
                  color: 'var(--primary-deep)',
                  minHeight: 56,
                  boxShadow: canSubmit ? `0 10px 30px ${_alpha('--gold', 0.35)}` : 'none',
                }}
                data-testid="membership-submit"
              >
                {submitting ? 'Submitting…' : 'Register interest'}
              </button>
            </form>
          )}
        </div>
      </div>
    </GuestLoyaltyThemeProvider>
  );
}


function OfferSection({ testid, icon: Icon, heading, subheading, entries }) {
  if (!entries || entries.length === 0) return null;
  return (
    <section className="mb-8" data-testid={testid}>
      <div className="flex items-center gap-2.5 mb-4"
           style={{ color: 'var(--gold)' }}>
        <Icon className="w-4 h-4" strokeWidth={1.75} />
        <h2 className="text-lg font-serif tracking-wide">{heading}</h2>
        <div className="flex-1 h-px" style={{ background: _alpha('--gold', 0.30) }} />
      </div>
      {subheading ? (
        <div className="text-[13px] opacity-70 mb-3 -mt-2 leading-snug"
             data-testid={`${testid}-subheading`}>
          {subheading}
        </div>
      ) : null}
      <div className="space-y-2.5">
        {entries.map((entry, i) => (
          <div
            key={i}
            className="flex items-start gap-3 rounded-xl px-4 py-4 border"
            style={{
              borderColor: _alpha('--gold', 0.20),
              background: _alpha('--surface', 0.6),
            }}
            data-testid={`${testid}-entry-${i}`}
          >
            <div className="flex-1 min-w-0">
              <div className="text-[16px] font-medium" style={{ color: 'var(--ink)' }}>
                {entry.title}
              </div>
              {entry.description ? (
                <div className="text-[13px] opacity-75 mt-1 leading-snug">
                  {entry.description}
                </div>
              ) : null}
            </div>
            {entry.value ? (
              <div className="text-lg font-semibold shrink-0 text-right"
                   style={{ color: 'var(--gold)' }}>
                {entry.value}
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </section>
  );
}


function PrivilegeCardSection({ card }) {
  return (
    <section className="mb-8" data-testid="section-privilege-card">
      <div className="flex items-center gap-2.5 mb-4"
           style={{ color: 'var(--gold)' }}>
        <CreditCard className="w-4 h-4" strokeWidth={1.75} />
        <h2 className="text-lg font-serif tracking-wide">Privilege card</h2>
        <div className="flex-1 h-px" style={{ background: _alpha('--gold', 0.30) }} />
      </div>
      <div
        className="rounded-xl px-5 py-5 border"
        style={{
          borderColor: _alpha('--gold', 0.35),
          background: `linear-gradient(135deg, ${_alpha('--gold', 0.10)} 0%, ${_alpha('--surface', 0.6)} 100%)`,
        }}
        data-testid="section-privilege-card-entry"
      >
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-[16px] font-medium" style={{ color: 'var(--ink)' }}>
              {card.title}
            </div>
            {card.description ? (
              <div className="text-[13px] opacity-75 mt-1 leading-snug">
                {card.description}
              </div>
            ) : null}
          </div>
          <div className="text-2xl font-semibold shrink-0 font-serif"
               style={{ color: 'var(--gold)' }}>
            {card.value}
          </div>
        </div>
      </div>
    </section>
  );
}


/** color-mix helper — same rationale as GuestHub._withAlpha. */
function _alpha(varName, alpha) {
  const clean = varName.replace(/^--/, '');
  const pct = Math.round(alpha * 100);
  return `color-mix(in srgb, var(--${clean}) ${pct}%, transparent)`;
}


/**
 * Stable data-testid for a section. Built-in sections keep the legacy
 * `section-<key>` ids so existing screenshots and tests keep working.
 * Custom sections get `section-custom-<id>` for uniqueness.
 */
function testidFor(section) {
  if (section.kind === 'builtin' && section.key === 'welcome_offers')  return 'section-welcome-offers';
  if (section.kind === 'builtin' && section.key === 'voucher_book')    return 'section-voucher-book';
  if (section.kind === 'builtin' && section.key === 'group_and_party') return 'section-group-and-party';
  if (section.kind === 'builtin' && section.key === 'buy_one_offers')  return 'section-buy-one-offers';
  return `section-custom-${section.id}`;
}
